import React, { useState, useEffect, useCallback } from 'react';
import { Wifi, WifiOff, AlertCircle, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

type ConnectionState = 'connected' | 'slow' | 'disconnected' | 'checking';

interface ServiceStatus {
  name: string;
  status: 'online' | 'offline' | 'slow';
  responseTime?: number;
}

const ConnectionStatus: React.FC = () => {
  const [connectionState, setConnectionState] = useState<ConnectionState>('connected');
  const [showDetails, setShowDetails] = useState(false);
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [lastChecked, setLastChecked] = useState<Date>(new Date());

  const checkConnection = useCallback(async () => {
    setConnectionState('checking');
    const serviceChecks: ServiceStatus[] = [];

    try {
      // Check internet connectivity
      if (!navigator.onLine) {
        setConnectionState('disconnected');
        serviceChecks.push({
          name: 'Internet',
          status: 'offline'
        });
        setServices(serviceChecks);
        return;
      }

      // Check Django backend (through Next.js API route)
      const startTime = Date.now();
      try {
        const response = await axios.get('/api/backend/health', {
          timeout: 5000,
          validateStatus: () => true // Accept any status
        });
        const responseTime = Date.now() - startTime;

        if (response.status === 200) {
          serviceChecks.push({
            name: 'Backend API',
            status: responseTime > 2000 ? 'slow' : 'online',
            responseTime
          });
        } else {
          serviceChecks.push({
            name: 'Backend API',
            status: 'offline',
            responseTime
          });
        }
      } catch (error) {
        serviceChecks.push({
          name: 'Backend API',
          status: 'offline'
        });
      }

      // Check MongoDB connection (via API)
      try {
        const mongoStart = Date.now();
        const mongoResponse = await axios.get('/api/mongo/health', {
          timeout: 5000,
          validateStatus: () => true
        });
        const mongoTime = Date.now() - mongoStart;

        serviceChecks.push({
          name: 'MongoDB',
          status: mongoResponse.status === 200
            ? (mongoTime > 2000 ? 'slow' : 'online')
            : 'offline',
          responseTime: mongoTime
        });
      } catch {
        serviceChecks.push({
          name: 'MongoDB',
          status: 'offline'
        });
      }

      // Determine overall connection state
      const allOffline = serviceChecks.every(s => s.status === 'offline');
      const someOffline = serviceChecks.some(s => s.status === 'offline');
      const someSlow = serviceChecks.some(s => s.status === 'slow');

      if (allOffline) {
        setConnectionState('disconnected');
      } else if (someOffline || someSlow) {
        setConnectionState('slow');
      } else {
        setConnectionState('connected');
      }

      setServices(serviceChecks);
      setLastChecked(new Date());
    } catch (error) {
      setConnectionState('disconnected');
      console.error('Connection check error:', error);
    }
  }, []);

  // Check connection on mount and periodically
  useEffect(() => {
    checkConnection();

    // Check every 30 seconds
    const interval = setInterval(checkConnection, 30000);

    // Also check on online/offline events
    window.addEventListener('online', checkConnection);
    window.addEventListener('offline', checkConnection);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', checkConnection);
      window.removeEventListener('offline', checkConnection);
    };
  }, [checkConnection]);

  // Get status color
  const getStatusColor = () => {
    switch (connectionState) {
      case 'connected':
        return 'bg-green-500';
      case 'slow':
        return 'bg-yellow-500';
      case 'disconnected':
        return 'bg-red-500';
      case 'checking':
        return 'bg-gray-400 animate-pulse';
      default:
        return 'bg-gray-400';
    }
  };

  // Get status icon
  const getStatusIcon = () => {
    switch (connectionState) {
      case 'connected':
        return <Wifi className="w-4 h-4 text-white" />;
      case 'slow':
        return <AlertCircle className="w-4 h-4 text-white" />;
      case 'disconnected':
        return <WifiOff className="w-4 h-4 text-white" />;
      case 'checking':
        return <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />;
      default:
        return <Wifi className="w-4 h-4 text-white" />;
    }
  };

  return (
    <>
      {/* Status Indicator */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className={`${getStatusColor()} p-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110`}
          title={`Connection: ${connectionState}`}
        >
          {getStatusIcon()}
        </button>

        {/* Pulse animation for issues */}
        {(connectionState === 'slow' || connectionState === 'disconnected') && (
          <span className="absolute -top-1 -left-1 flex h-3 w-3">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${getStatusColor()} opacity-75`}></span>
            <span className={`relative inline-flex rounded-full h-3 w-3 ${getStatusColor()}`}></span>
          </span>
        )}
      </div>

      {/* Details Modal */}
      {showDetails && (
        <div className="fixed bottom-16 right-4 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 min-w-[280px] max-w-sm">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-semibold text-gray-900 dark:text-white">Connection Status</h3>
            <button
              onClick={() => setShowDetails(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-2">
            {services.map((service) => (
              <div key={service.name} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-300">{service.name}</span>
                <div className="flex items-center gap-2">
                  {service.responseTime && service.status !== 'offline' && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {service.responseTime}ms
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      service.status === 'online'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                        : service.status === 'slow'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                    }`}
                  >
                    {service.status}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Last checked: {lastChecked.toLocaleTimeString()}
              </span>
              <button
                onClick={checkConnection}
                className="text-xs text-blue-600 hover:text-blue-800"
                disabled={connectionState === 'checking'}
              >
                {connectionState === 'checking' ? 'Checking...' : 'Refresh'}
              </button>
            </div>
          </div>

          {connectionState === 'disconnected' && (
            <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/30 rounded text-xs text-red-700 dark:text-red-300">
              Connection lost. Please check your internet connection.
            </div>
          )}

          {connectionState === 'slow' && (
            <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/30 rounded text-xs text-yellow-700 dark:text-yellow-300">
              Some services are slow. Performance may be affected.
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ConnectionStatus;